import { useCallback, useEffect, useState } from 'react';

// Zelfde normalisatie-idee as findRange in documentPdfPanel.tsx (safezone-web):
// woorden gescheiden door flexibele separator zodat evidence-zinnen matchen over
// whitespace-/quote-varianten heen. Bewust gedupliceerd: @vrzl/ui mag niet
// importeren uit de app.
const WORD_SEP = '[\\s"\'“”„‟‘’‚‛«»‹›`]+';

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export type TextIndex = {
  /** Lowercased concatenatie van alle text-nodes, met een separator op blokgrenzen. */
  text: string;
  /** Text-nodes in documentvolgorde. */
  nodes: Text[];
  /** Per karakter in `text`: index in `nodes`, of -1 voor een ingevoegde separator. */
  nodeIndexAt: number[];
  /** Per karakter in `text`: offset binnen die node's data (ongebruikt bij -1). */
  charOffsetAt: number[];
};

/**
 * Inline-tekstelementen: tekstnodes hierbinnen horen tot dezelfde leesregel en
 * mogen NIET door een separator gescheiden worden. Word splitst één woord
 * geregeld over meerdere runs (elk een eigen `<span>`), bv. "het" → "h" + "et".
 * Alles wat hier niet in staat is een blok-element; dát is wél een woordgrens.
 */
const INLINE_TAGS = new Set([
  'A', 'ABBR', 'B', 'BDI', 'BDO', 'CITE', 'CODE', 'DATA', 'DFN', 'EM', 'FONT',
  'I', 'KBD', 'LABEL', 'MARK', 'Q', 'RUBY', 'S', 'SAMP', 'SMALL', 'SPAN',
  'STRONG', 'SUB', 'SUP', 'TIME', 'TT', 'U', 'VAR', 'WBR',
]);

/**
 * Bouwt een doorzoekbare index over de gerenderde docx-preview DOM.
 *
 * docx-preview voegt geen whitespace-tekstnodes in tussen blok-elementen
 * (paragrafen, tabelcellen) — `display: block` beïnvloedt alleen de layout,
 * niet `Text.data`. Zonder separator zou het einde van de ene paragraaf direct
 * aan het begin van de volgende plakken. Maar een separator ná élke tekstnode
 * is óók fout: Word splitst woorden over meerdere inline-runs, dus dan zou
 * "aan het" als "aan h␣et" indexeren en zou zoeken op "aan he" niets vinden.
 *
 * Daarom loopt de walker over zowel elementen als tekst en wordt er alleen een
 * separator (nodeIndexAt = -1) ingevoegd op een echte woordgrens: bij het
 * betreden van een blok-element of een `<br>`. Inline-runs binnen dezelfde
 * regel worden aaneengeplakt zoals ze gelezen worden.
 */
export function buildTextIndex(root: HTMLElement): TextIndex {
  const nodes: Text[] = [];
  const nodeIndexAt: number[] = [];
  const charOffsetAt: number[] = [];
  let text = '';
  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
  );
  let pendingSeparator = false;
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = (node as Element).tagName;
      // <br> = harde regelovergang; elk niet-inline element = blokgrens.
      if (tag === 'BR' || !INLINE_TAGS.has(tag)) pendingSeparator = true;
      continue;
    }
    const t = node as Text;
    if (!t.data) continue;
    // Separator alleen op een echte grens, en nooit als leidend teken.
    if (pendingSeparator && text.length > 0) {
      text += ' ';
      nodeIndexAt.push(-1);
      charOffsetAt.push(-1);
    }
    pendingSeparator = false;
    const nodeIndex = nodes.length;
    nodes.push(t);
    for (let i = 0; i < t.data.length; i++) {
      text += t.data[i]!.toLowerCase();
      nodeIndexAt.push(nodeIndex);
      charOffsetAt.push(i);
    }
  }
  return { text, nodes, nodeIndexAt, charOffsetAt };
}

type MatchOffsets = { start: number; end: number };

/** Alle matches van `term` in de index, met word-boundary-tolerante regex. */
export function findMatchOffsets(index: TextIndex, term: string): MatchOffsets[] {
  const words = term.toLowerCase().split(new RegExp(WORD_SEP)).filter(Boolean);
  if (words.length === 0) return [];
  const pattern = new RegExp(words.map(escapeRegex).join(WORD_SEP), 'g');
  const matches: MatchOffsets[] = [];
  for (const m of index.text.matchAll(pattern)) {
    if (m.index !== undefined && m[0].length > 0) {
      matches.push({ start: m.index, end: m.index + m[0].length });
    }
  }
  return matches;
}

/**
 * Probeert kandidaat-teksten in volgorde tegen dezelfde index en geeft de
 * eerste niet-lege matchset terug. Ondersteunt de terugval van een
 * backend-platte-tekst naar een client-side gestripte variant van de
 * (mogelijk Markdown-) sentence: [{@link findMatchOffsets}] zelf blijft
 * single-term en onwetend van die volgorde.
 */
export function resolveMatchOffsets(
  index: TextIndex,
  rawText: string | null,
  candidates: string[],
): MatchOffsets[] {
  for (const candidate of candidates) {
    const trimmed = candidate.trim();
    if (!trimmed) continue;
    const firstWord = trimmed.toLowerCase().split(new RegExp(WORD_SEP)).filter(Boolean)[0];
    if (rawText && firstWord && !rawText.toLowerCase().includes(firstWord)) continue;
    const matches = findMatchOffsets(index, trimmed);
    if (matches.length > 0) return matches;
  }
  return [];
}

type NodeOffset = { node: Text; offset: number };

/** Zoekt vanaf `offset` voorwaarts naar het eerstvolgende echte (niet-separator) karakter. */
function locateForward(index: TextIndex, offset: number): NodeOffset | null {
  for (let i = offset; i < index.nodeIndexAt.length; i++) {
    const nodeIndex = index.nodeIndexAt[i];
    if (nodeIndex === undefined || nodeIndex < 0) continue;
    const node = index.nodes[nodeIndex];
    const charOffset = index.charOffsetAt[i];
    if (node && charOffset !== undefined) return { node, offset: charOffset };
  }
  return null;
}

/** Zoekt vanaf `offset` achterwaarts naar het eerstvolgende echte (niet-separator) karakter. */
function locateBackward(index: TextIndex, offset: number): NodeOffset | null {
  for (let i = offset; i >= 0; i--) {
    const nodeIndex = index.nodeIndexAt[i];
    if (nodeIndex === undefined || nodeIndex < 0) continue;
    const node = index.nodes[nodeIndex];
    const charOffset = index.charOffsetAt[i];
    if (node && charOffset !== undefined) return { node, offset: charOffset };
  }
  return null;
}

/** Vertaalt globale offsets naar een DOM Range over (mogelijk meerdere) text-nodes. */
export function offsetsToRange(index: TextIndex, match: MatchOffsets): Range | null {
  const start = locateForward(index, match.start);
  const end = locateBackward(index, match.end - 1); // -1: exclusieve end → binnen de laatste node
  if (!start || !end) return null;
  const range = document.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset + 1);
  return range;
}

export const DOCX_SEARCH_HIGHLIGHT = 'vdocx-search';
export const DOCX_ACTIVE_HIGHLIGHT = 'vdocx-search-active';

function highlightsSupported(): boolean {
  return typeof CSS !== 'undefined' && 'highlights' in CSS;
}

type UseDocxSearchArgs = {
  /** Element waarin docx-preview gerenderd heeft. */
  container: HTMLElement | null;
  /** True zodra de docx-preview render klaar is (processor status === 'ready'). */
  ready: boolean;
  /**
   * Kandidaat-teksten om te lokaliseren, in volgorde geprobeerd: de
   * zoekbalk-query is een lijst van één; een evidence-target levert
   * [backend-platte-tekst, client-side gestripte sentence] als terugval.
   */
  terms: string[];
  /** Mammoth rawText — snelle bestaat-de-term-check vóór de DOM-walk. */
  rawText: string | null;
};

/**
 * Interactielaag: lokaliseert de eerste matchende kandidaat uit `terms` in de
 * gerenderde DOM, highlight alle matches via de CSS Custom Highlight API
 * (muteert de docx-preview DOM niet) en scrolt de actieve match in beeld.
 * Zoekbalk en evidence-target delen dezelfde locate → highlight → scroll
 * routine.
 */
export function useDocxSearch({ container, ready, terms, rawText }: UseDocxSearchArgs) {
  const [matches, setMatches] = useState<Range[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  // Lokaliseren: index en ranges (her)berekenen zodra render of terms wijzigt.
  // `highlightTarget` gezet vóór de render klaar is → dit effect draait vanzelf
  // opnieuw wanneer `ready` true wordt (queued-gedrag uit de spec).
  useEffect(() => {
    if (!container || !ready || terms.length === 0) {
      setMatches([]);
      return;
    }
    const index = buildTextIndex(container);
    const ranges = resolveMatchOffsets(index, rawText, terms)
      .map((m) => offsetsToRange(index, m))
      .filter((r): r is Range => r !== null);
    setMatches(ranges);
    setActiveIndex(0);
  }, [container, ready, terms, rawText]);

  // Highlighten: alle matches + de actieve match schilderen zonder DOM-mutatie.
  useEffect(() => {
    if (!highlightsSupported()) return;
    if (matches.length === 0) {
      CSS.highlights.delete(DOCX_SEARCH_HIGHLIGHT);
      CSS.highlights.delete(DOCX_ACTIVE_HIGHLIGHT);
      return;
    }
    CSS.highlights.set(DOCX_SEARCH_HIGHLIGHT, new Highlight(...matches));
    const active = matches[activeIndex];
    if (active) {
      CSS.highlights.set(DOCX_ACTIVE_HIGHLIGHT, new Highlight(active));
    }
    return () => {
      CSS.highlights.delete(DOCX_SEARCH_HIGHLIGHT);
      CSS.highlights.delete(DOCX_ACTIVE_HIGHLIGHT);
    };
  }, [matches, activeIndex]);

  // Scrollen: actieve match in beeld brengen.
  useEffect(() => {
    const active = matches[activeIndex];
    if (!active) return;
    active.startContainer.parentElement?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [matches, activeIndex]);

  const goNext = useCallback(() => {
    setActiveIndex((i) => (matches.length === 0 ? 0 : (i + 1) % matches.length));
  }, [matches.length]);

  const goPrev = useCallback(() => {
    setActiveIndex((i) => (matches.length === 0 ? 0 : (i - 1 + matches.length) % matches.length));
  }, [matches.length]);

  return { matchCount: matches.length, activeIndex, goNext, goPrev };
}
